import { FC } from 'react';

import {
  BrandIconProps,
  BrandLogoProps,
} from '@/components/shared/logos/props';

export enum OrderConfirmationBrands {
  bilka = 'BLK-SAP-ID',
  foetex = 'FTX-SAP-ID',
  br = 'FBR-SAP-ID',
  unknown = 'UKNOWN-SAP-ID',
}

interface GenericBrandProperties<
  BrandKey,
  BrandName,
  CustomerProgramKey extends string | undefined,
> {
  key: BrandKey;
  label: BrandName;
  idCode: string | undefined;
  logo: FC<BrandLogoProps>;
  icon: FC<BrandIconProps>;
  algoliaIndex?: string;
  brandSite?: string;
  orderConfirmationKey: OrderConfirmationBrands;

  loyalty: {
    customerProgram: {
      signupDateKey: `${CustomerProgramKey}SignupDate`;
      membershipNrKey: `${CustomerProgramKey}MembershipNr`;
    };
    storeBoxProgram?: CustomerProgramKey extends string
      ? Uppercase<CustomerProgramKey>
      : undefined;
    areMemberOffersBurnable: boolean;
    isWalletAvailable: boolean;
    isProductDataAvailable: boolean;
    isRefundAvailable: boolean;
  };

  orders: boolean;
}

interface GenericBrandPropertiesWithoutCustomerProgram<
  BrandKey,
  BrandName,
> extends Omit<
  GenericBrandProperties<BrandKey, BrandName, undefined>,
  'loyalty'
> {
  loyalty: false;
}

export type BrandProperties =
  | GenericBrandProperties<'bilka', 'Bilka', 'cpblk'>
  | GenericBrandPropertiesWithoutCustomerProgram<'br', 'BR'>
  | GenericBrandProperties<'carlsjr', "Carl's Jr.", 'cpcjr'>
  | GenericBrandProperties<'foetex', 'Føtex', 'cpfot'>
  | GenericBrandProperties<'nettode', 'Netto DE', 'cpnde'>
  | GenericBrandProperties<'nettodk', 'Netto DK', 'cpndk'>
  | GenericBrandPropertiesWithoutCustomerProgram<'nettopl', 'Netto PL'>
  | GenericBrandProperties<'salling', 'Salling', 'cpsal'>
  | GenericBrandPropertiesWithoutCustomerProgram<'flowr', 'Flowr'>;

type InferGenericBrandPropertiesType<T> =
  T extends GenericBrandProperties<
    infer BrandKey,
    infer BrandName,
    infer CustomerProgramKey
  >
    ? CustomerProgramKey extends undefined
      ? never
      : {
          BrandKey: BrandKey;
          BrandName: BrandName;
          CustomerProgramKey: CustomerProgramKey;
        }
    : never;

type InferBrandProperties = InferGenericBrandPropertiesType<BrandProperties>;

export type BrandKey = BrandProperties['key'];

export const isBrandKey = (brand: any): brand is BrandKey => {
  const brands: BrandKey[] = [
    'bilka',
    'br',
    'carlsjr',
    'foetex',
    'nettode',
    'nettodk',
    'nettopl',
    'salling',
    'flowr',
  ];
  return brands.includes(brand);
};

export type CustomerProgramKey = InferBrandProperties['CustomerProgramKey'];
export type CustomerProgramMembershipNr = `${CustomerProgramKey}MembershipNr`;
export type CustomerProgramSignupDate = `${CustomerProgramKey}SignupDate`;

export const getPaymentGatewayBrand = (brand: string | undefined | null) => {
  switch (brand) {
    case 'bilka':
      return 'bilkadk';
    case 'foetex':
      return 'foetexdk';
    case 'br':
      return 'brdk';
    default:
      return undefined;
  }
};

export class Brand {
  constructor(private readonly brandProperties: BrandProperties) {}

  get key() {
    return this.brandProperties.key;
  }

  get label() {
    return this.brandProperties.label;
  }

  get idCode() {
    return this.brandProperties.idCode;
  }

  get logo() {
    return this.brandProperties.logo;
  }

  get icon() {
    return this.brandProperties.icon;
  }

  get algoliaIndex(): string | undefined {
    return this.brandProperties.algoliaIndex;
  }

  get orderConfirmationKey(): OrderConfirmationBrands {
    return this.brandProperties.orderConfirmationKey;
  }

  get brandSite(): string | undefined {
    return this.brandProperties.brandSite;
  }

  get storeBoxProgram() {
    if (this.brandProperties.loyalty) {
      return this.brandProperties.loyalty.storeBoxProgram;
    } else {
      throw new Error('store box program not supported for this brand');
    }
  }

  get isSupportedForLoyalty() {
    return (
      'loyalty' in this.brandProperties && Boolean(this.brandProperties.loyalty)
    );
  }

  get isSupportedForOrders() {
    return this.brandProperties.orders ?? false;
  }

  get isSupportedForStoreBox() {
    return this.brandProperties.loyalty
      ? Boolean(this.brandProperties.loyalty?.storeBoxProgram)
      : false;
  }

  get areMemberOffersBurnable() {
    if (this.brandProperties.loyalty) {
      return this.brandProperties.loyalty.areMemberOffersBurnable;
    }
    return false;
  }

  get isWalletAvailable() {
    if (this.brandProperties.loyalty) {
      return this.brandProperties.loyalty.isWalletAvailable;
    }
    return false;
  }

  get isProductDataAvailable() {
    if (this.brandProperties.loyalty) {
      return this.brandProperties.loyalty.isProductDataAvailable;
    }
    return false;
  }

  get isRefundAvailable() {
    if (this.brandProperties.loyalty) {
      return this.brandProperties.loyalty.isRefundAvailable;
    }
    return false;
  }
}
